import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  motion,
  useMotionValue,
  useSpring,
  useTransform,
  useScroll,
} from "framer-motion";
import {
  Footprints,
  BellOff,
  Repeat2,
  Target,
  Activity,
  AlarmClockMinus,
  ArrowRight,
  ArrowDown,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Marquee } from "@/components/Marquee";
import { Reveal, MaskedLine, EASE } from "@/components/Reveal";
import { PhoneMockup } from "@/components/PhoneMockup";
import { StepSimulator } from "@/components/StepSimulator";

const HeroPhone = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 120, damping: 18 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={() => {
        mx.set(0);
        my.set(0);
      }}
      className="relative [perspective:1200px]"
    >
      <div className="pointer-events-none absolute left-1/2 top-1/2 h-[420px] w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-terra/25 blur-[100px]" />
      <svg
        className="animate-spin-slow pointer-events-none absolute -left-16 -top-16 h-44 w-44 text-terra/50"
        viewBox="0 0 100 100"
        fill="none"
      >
        <circle cx="50" cy="50" r="48" stroke="currentColor" strokeWidth="0.8" strokeDasharray="4 6" />
      </svg>
      <motion.div
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.1, delay: 0.5, ease: EASE }}
      >
        <PhoneMockup screen="mission" />
      </motion.div>
    </div>
  );
};

const Hero = () => (
  <section className="relative overflow-hidden pt-32 pb-20 sm:pt-40 lg:pb-28">
    <div className="mx-auto grid max-w-7xl items-center gap-16 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr] lg:px-8">
      <div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
          data-testid="hero-eyebrow"
          className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra"
        >
          Moove — Walk to wake up
        </motion.p>

        <h1
          data-testid="hero-headline"
          className="mt-6 font-serif text-6xl leading-[1.02] tracking-tight text-ink sm:text-7xl lg:text-[6.5rem]"
        >
          <MaskedLine delay={0.2}>Walk to</MaskedLine>
          <MaskedLine delay={0.35} className="italic text-terra">
            wake up.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6, ease: EASE }}
          className="mt-8 max-w-md text-lg leading-relaxed text-mutedwarm"
        >
          The alarm that only stops when you start moving. Set your step goal,
          go to sleep — Moove keeps ringing until you're out of bed and walking.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.75, ease: EASE }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <span
            data-testid="hero-download-button"
            className="cursor-default rounded-full bg-ink px-7 py-3.5 text-sm font-semibold text-cream"
          >
            Coming soon on iOS
          </span>
          <Link
            to="/#how-it-works"
            data-testid="hero-demo-button"
            className="group flex items-center gap-2 rounded-full border border-ink/20 px-7 py-3.5 text-sm font-semibold text-ink transition-all duration-300 hover:border-terra hover:text-terra"
          >
            See how it works
            <ArrowDown size={15} className="transition-transform duration-300 group-hover:translate-y-0.5" />
          </Link>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.95 }}
          className="mt-8 flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-mutedwarm"
        >
          <span>iOS</span>
          <span className="h-1 w-1 rounded-full bg-terra" />
          <span>Free for 7 days</span>
          <span className="h-1 w-1 rounded-full bg-terra" />
          <span>No puzzles</span>
        </motion.p>
      </div>

      <div className="flex justify-center lg:justify-end">
        <HeroPhone />
      </div>
    </div>
  </section>
);

const Manifesto = () => (
  <section className="py-24 sm:py-32">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <Reveal>
        <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
          The idea
        </p>
      </Reveal>
      <h2 className="mt-8 font-serif text-4xl leading-[1.15] tracking-tight text-ink sm:text-5xl lg:text-6xl">
        <MaskedLine delay={0.1}>Waking up is easy.</MaskedLine>
        <MaskedLine delay={0.22}>
          Getting out of bed is <span className="italic text-terra">the hard part.</span>
        </MaskedLine>
      </h2>
      <Reveal delay={0.25} className="mt-10 max-w-2xl">
        <p className="text-lg leading-relaxed text-mutedwarm">
          No puzzles. No complicated challenges. Just one simple rule: want the
          alarm to stop? <span className="font-semibold text-ink">Moove.</span>{" "}
          Built for heavy sleepers, chronic snoozers, and anyone who needs a
          little extra push to get out of bed.
        </p>
      </Reveal>
    </div>
  </section>
);

const steps = [
  {
    n: "01",
    title: "Set your alarm",
    body: "Choose a wake-up time, repeat days, and how many steps it takes to switch it off.",
  },
  {
    n: "02",
    title: "Get up and walk",
    body: "When it rings, the only way to stop it is to start moving. Your steps are counted live.",
  },
  {
    n: "03",
    title: "Hit your goal",
    body: "Reach your step goal and the alarm stops. Snooze buys you minutes — walking buys you the morning.",
  },
];

const HowItWorks = () => (
  <section id="how-it-works" data-testid="how-it-works-section" className="border-t border-line py-24 sm:py-32">
    <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-2 lg:px-8">
      <div className="lg:sticky lg:top-28 lg:self-start">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
            How it works
          </p>
        </Reveal>
        <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Three</MaskedLine>
          <MaskedLine delay={0.2} className="italic text-terra">
            simple steps.
          </MaskedLine>
        </h2>
        <Reveal delay={0.25}>
          <p className="mt-6 max-w-sm text-base leading-relaxed text-mutedwarm">
            Set your alarm, start walking, and hit your goal. That's the whole
            product — and that's why it works.
          </p>
        </Reveal>
        <Reveal delay={0.35} className="mt-12 hidden lg:block">
          <PhoneMockup screen="steps" />
        </Reveal>
      </div>

      <div>
        {steps.map((s, i) => (
          <Reveal key={s.n} delay={i * 0.12}>
            <div
              data-testid={`how-step-${s.n}`}
              className="group border-t border-line py-10 transition-colors duration-500 first:border-t-0 first:pt-0 hover:bg-sand/40 lg:first:border-t"
            >
              <p className="font-serif text-5xl font-light italic text-terra/90 transition-transform duration-500 group-hover:-translate-y-1 sm:text-6xl">
                {s.n}
              </p>
              <h3 className="mt-5 font-serif text-2xl text-ink sm:text-3xl">{s.title}</h3>
              <p className="mt-3 max-w-md text-base leading-relaxed text-mutedwarm">{s.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const features = [
  {
    icon: Footprints,
    title: "Motion-verified steps",
    body: "Steps are counted from your phone's motion sensors while the alarm rings. Lying still won't silence it.",
  },
  {
    icon: AlarmClockMinus,
    title: "A snooze that fights back",
    body: "Yes, you can snooze — for a few minutes. Then Moove rings again, and your step goal is still waiting.",
  },
  {
    icon: Target,
    title: "Custom step goals",
    body: "Thirty steps to the bathroom or fifty around the block — you decide how far morning-you has to go.",
  },
  {
    icon: Repeat2,
    title: "Repeat schedules",
    body: "Weekday alarms, gym days, one-off early flights. Set each alarm with its own step goal.",
  },
  {
    icon: BellOff,
    title: "No games, no gimmicks",
    body: "No math puzzles, no QR scans, no memory tests. One rule: walk, and the ringing stops.",
  },
  {
    icon: Activity,
    title: "Live progress",
    body: "Watch your remaining steps count down in real time as you walk the alarm into silence.",
  },
];

const Features = () => (
  <section id="features" data-testid="features-section" className="border-t border-line bg-sand/40 py-24 sm:py-32">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
            Features
          </p>
        </Reveal>
        <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Ruthlessly</MaskedLine>
          <MaskedLine delay={0.2} className="italic text-terra">simple.</MaskedLine>
        </h2>
      </div>

      <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {features.map((f, i) => (
          <Reveal key={f.title} delay={(i % 3) * 0.1}>
            <div
              data-testid={`feature-card-${i + 1}`}
              className="group h-full rounded-3xl border border-line bg-cardcream p-8 transition-all duration-500 hover:-translate-y-1 hover:border-terra/40 hover:shadow-[0_24px_48px_-24px_rgba(224,101,75,0.25)]"
            >
              <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-terrasoft transition-colors duration-500 group-hover:bg-terra">
                <f.icon size={22} className="text-terra transition-colors duration-500 group-hover:text-cream" />
              </span>
              <h3 className="mt-6 font-serif text-xl text-ink">{f.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-mutedwarm">{f.body}</p>
            </div>
          </Reveal>
        ))}
      </div>

      <Reveal delay={0.15} className="mt-16 flex justify-center">
        <PhoneMockup screen="alarms" />
      </Reveal>
    </div>
  </section>
);

const Demo = () => (
  <section data-testid="demo-section" className="py-24 sm:py-32">
    <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
      <div className="mb-12 text-center">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
            Don't take our word for it
          </p>
        </Reveal>
        <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Feel the</MaskedLine>
          <MaskedLine delay={0.2} className="italic text-terra">mission.</MaskedLine>
        </h2>
      </div>
      <Reveal delay={0.2}>
        <StepSimulator />
      </Reveal>
    </div>
  </section>
);

const plans = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$3.99",
    per: "per month",
    note: "After your free 7-day trial",
    badge: null,
  },
  {
    id: "annual",
    name: "Annual",
    price: "$29.99",
    per: "per year",
    note: "After your free 7-day trial",
    badge: "Save 37%",
  },
];

const Pricing = () => (
  <section id="pricing" data-testid="pricing-section" className="border-t border-line bg-sand/40 py-24 sm:py-32">
    <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
      <div className="text-center">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
            Pricing
          </p>
        </Reveal>
        <h2 className="mt-6 font-serif text-4xl leading-[1.1] tracking-tight text-ink sm:text-5xl">
          <MaskedLine delay={0.1}>Free for 7 days.</MaskedLine>
          <MaskedLine delay={0.2} className="italic text-terra">Then pocket change.</MaskedLine>
        </h2>
        <Reveal delay={0.25}>
          <p className="mx-auto mt-6 max-w-xl text-base leading-relaxed text-mutedwarm">
            Every plan starts with a free 7-day trial. Subscriptions are handled
            securely by Apple — cancel anytime.
          </p>
        </Reveal>
      </div>

      <div className="mx-auto mt-14 grid max-w-3xl gap-6 sm:grid-cols-2">
        {plans.map((p, i) => (
          <Reveal key={p.id} delay={0.1 + i * 0.12}>
            <div
              data-testid={`pricing-card-${p.id}`}
              className={`group relative h-full rounded-3xl border p-9 text-center transition-all duration-500 hover:-translate-y-1 ${
                p.badge
                  ? "border-terra/50 bg-espresso text-cream hover:shadow-[0_24px_48px_-24px_rgba(224,101,75,0.45)]"
                  : "border-line bg-cardcream hover:border-terra/40 hover:shadow-[0_24px_48px_-24px_rgba(224,101,75,0.25)]"
              }`}
            >
              {p.badge && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-terra px-4 py-1 text-xs font-semibold text-cream">
                  {p.badge}
                </span>
              )}
              <p className={`font-mono text-xs font-semibold uppercase tracking-[0.25em] ${p.badge ? "text-terra" : "text-mutedwarm"}`}>
                {p.name}
              </p>
              <p className={`mt-5 font-serif text-6xl font-light tracking-tight ${p.badge ? "text-cream" : "text-ink"}`}>
                {p.price}
              </p>
              <p className={`mt-2 text-sm ${p.badge ? "text-cream/60" : "text-mutedwarm"}`}>{p.per}</p>
              <p className={`mt-6 border-t pt-5 text-xs ${p.badge ? "border-cream/15 text-cream/50" : "border-line text-mutedwarm"}`}>
                {p.note}
              </p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CTA = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/waitlist`, { email });
      setStatus("done");
      toast.success("You're on the list — we'll email you at launch.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't add you right now. Try again soon.");
    }
  };

  return (
    <section ref={ref} className="relative overflow-hidden bg-espresso py-28 text-cream sm:py-36">
      <motion.div
        style={{ y }}
        className="pointer-events-none absolute -left-32 top-0 h-96 w-96 rounded-full bg-terra/15 blur-[120px]"
      />
      <div className="relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6">
        <Reveal>
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.3em] text-terra">
            Moove — for iOS
          </p>
        </Reveal>
        <h2 className="mt-8 font-serif text-5xl leading-[1.05] tracking-tight sm:text-6xl lg:text-7xl">
          <MaskedLine delay={0.1}>Want the alarm to stop?</MaskedLine>
          <MaskedLine delay={0.25} className="italic text-terra">Moove.</MaskedLine>
        </h2>
        <Reveal delay={0.3}>
          <div className="mt-12 flex flex-col items-center gap-10">
            <span
              data-testid="cta-download-button"
              className="cursor-default rounded-full bg-terra px-8 py-4 text-sm font-semibold text-cream"
            >
              Coming soon on the App Store
            </span>
            {status === "done" ? (
              <p data-testid="waitlist-success" className="font-serif text-2xl italic text-terra">
                You're on the list. See you at sunrise.
              </p>
            ) : (
              <form data-testid="waitlist-form" onSubmit={submit} className="w-full max-w-md">
                <p className="mb-4 font-mono text-[11px] font-semibold uppercase tracking-[0.3em] text-cream/50">
                  Get notified at launch
                </p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="waitlist-email-input"
                    placeholder="you@example.com"
                    className="w-full rounded-full border border-cream/25 bg-cream/10 px-6 py-3.5 text-sm text-cream outline-none transition-colors duration-300 placeholder:text-cream/40 focus:border-terra"
                  />
                  <button
                    type="submit"
                    data-testid="waitlist-submit-button"
                    disabled={status === "sending"}
                    className="flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-cream px-7 py-3.5 text-sm font-semibold text-espresso transition-all duration-300 hover:scale-[1.03] disabled:opacity-60"
                  >
                    {status === "sending" ? (
                      "Joining…"
                    ) : (
                      <>
                        Notify me <ArrowRight size={15} />
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function Landing() {
  return (
    <div data-testid="landing-page" className="bg-cream">
      <Navbar />
      <main>
        <Hero />
        <Marquee />
        <Manifesto />
        <HowItWorks />
        <Features />
        <Pricing />
        <Demo />
        <CTA />
      </main>
      <Footer />
    </div>
  );
}
